// Language picker (English / Español). Language names are shown in their
// own language so a player can always find theirs.

import { Button } from '@aod/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@aod/ui/components/ui/dropdown-menu';
import { LOCALES, LOCALE_FLAGS, LOCALE_NAMES, isLocale, useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { useSettings } from '../../settings/useSettings.js';
import { TOPBAR_CONTROL_OUTLINE } from './controlStyle.js';

// The chosen language is shown by a silver row, not a bullet: the radio indicator is hidden
// and its left gutter given back.
const SELECTED_ROW =
  'pl-2 [&>span:first-child]:hidden data-[state=checked]:bg-slate-300 data-[state=checked]:font-medium data-[state=checked]:text-slate-900 dark:data-[state=checked]:bg-slate-400/30 dark:data-[state=checked]:text-slate-100';

export function LocaleSelect() {
  const t = useT();
  const locale = useSettings((s) => s.locale);
  const setLocale = useSettings((s) => s.setLocale);
  const label = `${t('locale.label')}: ${LOCALE_NAMES[locale]}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="locale-select"
          {...appDom({
            id: 'locale-select',
            type: 'Select',
            role: 'select',
            scope: 'header',
            label: 'Language',
          })}
          data-locale={locale}
          aria-label={label}
          title={label}
          className={`h-8 px-2 ${TOPBAR_CONTROL_OUTLINE}`}
        >
          <span
            className="text-base leading-none"
            aria-hidden
            data-testid={`locale-flag-${locale}`}
          >
            {LOCALE_FLAGS[locale]}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[9rem]"
        data-testid="locale-menu"
        {...appDom({ id: 'locale-menu', type: 'Dropdown', scope: 'menu' })}
      >
        <DropdownMenuLabel>{t('locale.label')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={locale}
          onValueChange={(v) => {
            if (isLocale(v)) setLocale(v);
          }}
        >
          {LOCALES.map((code) => (
            <DropdownMenuRadioItem
              key={code}
              value={code}
              lang={code}
              className={SELECTED_ROW}
              data-testid={`locale-${code}`}
              {...appDom({ id: `locale-${code}`, role: 'button', scope: 'menu' })}
            >
              <span aria-hidden className="mr-1">
                {LOCALE_FLAGS[code]}
              </span>
              {LOCALE_NAMES[code]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
